import { ReactNode } from 'react';
import ScreenHeader from './screen-header';

export interface PageHeaderStat {
  label: string;
  value: number | string;
  icon?: string;
  hint?: string;
}

interface PageHeaderProps {
  /** PrimeIcon class, e.g. 'pi pi-th-large'. */
  icon: string;
  /** Tailwind background for the icon tile; defaults to the shared indigo. */
  iconBgColor?: string;
  title: string;
  subtitle?: string;
  /** Compact metric chips shown on the right, before the actions. */
  stats?: PageHeaderStat[];
  /** Right-aligned actions (typically the primary button). */
  actions?: ReactNode;
  /** When rendered inside a group tab: hide the title/subtitle (the group shows
   *  them) but KEEP the actions row so the primary action (e.g. Create) stays. */
  embedded?: boolean;
  testId?: string;
}

/**
 * Page header for a directly-routed list screen: the shared ScreenHeader plus
 * optional compact metric chips and the primary action on the right.
 *
 * The title/tile styling deliberately lives in ScreenHeader rather than here, so
 * a standalone screen and a tabbed group screen present identically.
 */
const PageHeader = ({ icon, iconBgColor, title, subtitle, stats, actions, embedded, testId }: PageHeaderProps) => {
  // Embedded in a group tab: the group already shows the title, so render only the
  // actions row (keeps Create/primary actions reachable). Nothing if no actions.
  if (embedded) {
    return actions ? (
      <div className="mb-3 flex justify-end" data-testid={testId}>{actions}</div>
    ) : null;
  }
  return (
    <ScreenHeader
      icon={icon}
      iconBgColor={iconBgColor}
      title={title}
      subtitle={subtitle}
      testId={testId}
      right={(stats?.length || actions) ? (
        <>
          {stats?.map((s, i) => (
            <div
              key={`${s.label}-${i}`}
              className="inline-flex items-center gap-2.5 rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2 dark:border-gray-700 dark:bg-gray-800/60"
            >
              {s.icon && (
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400">
                  <i className={`${s.icon} text-sm`} />
                </span>
              )}
              <span className="leading-tight">
                <span className="block text-base font-semibold text-ink">
                  {typeof s.value === 'number' ? s.value.toLocaleString() : s.value}
                </span>
                <span className="block text-caption font-medium text-ink-muted">
                  {s.label}
                  {s.hint ? ` · ${s.hint}` : ''}
                </span>
              </span>
            </div>
          ))}
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </>
      ) : undefined}
    />
  );
};

export default PageHeader;
